import { api } from '@/boot/axios'
import type { EmployeePermission } from '@/enums/EmployeePermission'
import type { PersonRole } from '@/enums/PersonRole'
import { PersonRoleUI } from '@/enumUI/PersonRoleUI'
import { usePwa } from '@/PWA'
import type { Person } from '@/types/Person/Person'
import { clearStorableRefStorage } from '@/utils/storableRef'
import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'
import { Loading } from 'quasar'
import { computed, ref } from 'vue'
//import { useRouter } from 'vue-router';
//import { api } from '@/boot/axios';
//import { AxiosError } from 'axios';

export const useAuthStore = defineStore('auth', () => {
  const person = ref<Required<Person & { guidforopenday?: string }> | null>(null)
  const isLoggedIn = computed(() => !!person.value)

  const { pushSubscribe, pushUnsubscribe } = usePwa()

  const SENTRY_DSN = import.meta.env.CLI_SENTRY_DSN
  const login = async (data: Required<Person & { guidforopenday?: string }>) => {
    person.value = person.value ?? ({} as Required<Person & { guidforopenday?: string }>)
    Object.assign(person.value, data)
    return await pushSubscribe()
  }

  const logout = async () => {
    Loading.show()
    await pushUnsubscribe()
    const logoutResponse = await api.post('auth/logout')
    clearStorableRefStorage()
    window.location.replace(logoutResponse.data ? `/m/people/${logoutResponse.data}` : '/')
  }

  // const $router = useRouter();
  const fetch = async () => {
    try {
      const request = await api.get('/api/user')
      person.value = request.data.data
      if (SENTRY_DSN && person.value?.id && !person.value?.original_person) {
        Sentry.setUser({
          id: person.value.id,
          roles: person.value?.roles?.map(({ role }) => PersonRoleUI[role].label).join(', '),
          //card_url: $router.resolve({ name: 'manager.person', params: { personId: person.value.id } }).href
        })
      }
      return person.value
    } catch {
      /* empty */
    }
  }

  return {
    logout,
    person,
    login,
    isLoggedIn,
    fetch,
    hasEmployeePermission(...permissions: EmployeePermission[]): boolean {
      permissions = Array.isArray(permissions[0]) ? permissions[0] : permissions
      return permissions.every((permission) =>
        person.value?.employee?.permissions?.includes(permission),
      )
    },
    hasAnyEmployeePermission(...permissions: EmployeePermission[]): boolean {
      permissions = Array.isArray(permissions[0]) ? permissions[0] : permissions
      return permissions.some((permission) =>
        person.value?.employee?.permissions?.includes(permission),
      )
    },
    hasRole(...roles: PersonRole[]): boolean {
      roles = Array.isArray(roles[0]) ? roles[0] : roles
      return roles.every((role) => person.value?.roles?.some((r) => r.role === role))
    },
    hasAnyRole(...roles: PersonRole[]): boolean {
      roles = Array.isArray(roles[0]) ? roles[0] : roles
      return roles.some((role) => person.value?.roles?.some((r) => r.role === role))
    },
  }
})
